import { Controller } from "@hotwired/stimulus"

// Toggle a recipe save for the signed-in user (Phase 1 item 1.9).
// POST = save; DELETE = unsave. Anonymous users get a sign-in CTA.
// Re-uses the same pattern as fridge_controller for auth-required and
// limit-reached responses.
export default class extends Controller {
  static targets = [ "button", "icon", "label", "hint" ]
  static values = {
    saveUrl: String,
    unsaveUrl: String,
    saved: Boolean
  }

  toggle() {
    this.savedValue ? this._call("DELETE", this.unsaveUrlValue)
                    : this._call("POST",   this.saveUrlValue)
  }

  async _call(method, url) {
    this.buttonTarget.disabled = true
    let data, ok
    try {
      const res = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        }
      })
      data = await res.json().catch(() => ({}))
      ok = res.ok
    } catch (_e) {
      this._showHint("Couldn't reach the server — please try again.", "error")
      this.buttonTarget.disabled = false
      return
    }

    this.buttonTarget.disabled = false

    if (ok && data.status === "saved") {
      this.savedValue = true
      this._renderSaved()
      this._showHint("Saved to your book.", "ok")
    } else if (ok && data.status === "unsaved") {
      this.savedValue = false
      this._renderUnsaved()
      this._showHint("Removed from your book.", "ok")
    } else if (data.status === "auth_required") {
      this._showHint(
        `Sign in to save recipes — <a href="${data.sign_in_url}" class="underline font-semibold">sign in</a> or <a href="${data.sign_up_url}" class="underline font-semibold">create an account</a>.`,
        "warn",
        { allowHtml: true }
      )
    } else if (data.status === "limit_reached") {
      this._showHint(
        `You've hit the free save limit (${data.limit}). <a href="${data.upgrade_url || "/upgrade"}" class="underline font-semibold">Upgrade for unlimited saves</a>.`,
        "warn",
        { allowHtml: true }
      )
    } else {
      this._showHint("Something went wrong — please try again.", "error")
    }
  }

  _renderSaved() {
    this.buttonTarget.classList.remove("bg-white", "text-amber-800", "border-amber-300", "hover:bg-amber-50")
    this.buttonTarget.classList.add("bg-amber-700", "text-white", "border-amber-700", "hover:bg-amber-800")
    if (this.hasLabelTarget) this.labelTarget.textContent = "Saved to your book"
  }

  _renderUnsaved() {
    this.buttonTarget.classList.remove("bg-amber-700", "text-white", "border-amber-700", "hover:bg-amber-800")
    this.buttonTarget.classList.add("bg-white", "text-amber-800", "border-amber-300", "hover:bg-amber-50")
    if (this.hasLabelTarget) this.labelTarget.textContent = "Save to My Breakfast Book"
  }

  _showHint(message, kind, { allowHtml = false } = {}) {
    if (!this.hasHintTarget) return
    if (allowHtml) {
      this.hintTarget.innerHTML = message
    } else {
      this.hintTarget.textContent = message
    }
    this.hintTarget.classList.remove("text-amber-700/70", "text-red-700", "text-emerald-700")
    if (kind === "ok") {
      this.hintTarget.classList.add("text-emerald-700")
    } else if (kind === "error") {
      this.hintTarget.classList.add("text-red-700")
    } else {
      this.hintTarget.classList.add("text-amber-700/70")
    }
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
